<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";

import { useElements } from "../../../composables/elements";
import { gradientToCss } from "../../../composables/styleGradient";

import AddNewElementButton from "./AddNewElementButton.vue";
import EditElementButton from "./EditElementButton.vue";
import ReorderElement from "./ReorderElement.vue";
import CopyElementButton from "./CopyElementButton.vue";
import VisibilityElement from "./VisibilityElement.vue";
import StyleCopyButton from "./StyleCopyButton.vue";

const { t } = useI18n();
const { renderElement, storeElement, updateElement } = useElements();

const props = defineProps({
  websiteId: { type: [String, Number], required: true },
  pageId: { type: [String, Number], required: true },
  type: { type: String, required: true },
  data: { type: Object, required: true },
  prev: { type: Object, default: null },
  next: { type: Object, default: null },
  hideStyleCopy: { type: Boolean, default: false },
});

// Локальный UI state проще как ref’ы
const isActive = ref(false);

// Безопасный доступ к данным элемента и попап-настройкам
const elementData = computed(() => props.data ?? {});
const popup = computed(() => elementData.value?.style?.additional?.popup ?? null);

const popupStyle = computed(() => {
  const el = popup.value?.style?.element;
  if (!el) return {};
  const gradient = gradientToCss(el.backgroundGradient);
  return gradient ? { ...el, background: gradient } : el;
});

// Классы выравнивания контейнера кнопки попапа
const popupAlignClass = computed(() => {
  const pos = popup.value?.style?.position || "left";
  return {
    "text-start": pos === "left",
    "text-center": pos === "center",
    "text-end": pos === "right",
  };
});

// Хэндлеры
const handleToggleActive = () => {
  isActive.value = !isActive.value;
};

// Универсально прокинем payload, если дочерний эмитит данные
const handleCreate = (...args) => {
  // Если дочерний присылает готовую структуру — отдадим её.
  // Иначе упадём на дефолт: создать «после prev» для текущей страницы.
  if (args.length) return storeElement(...args);
  return storeElement({
    pageId: props.pageId,
    afterElementId: props.prev?.id ?? 0,
    element: elementData.value?.element ?? {},
    style: elementData.value?.style ?? {},
    type: props.type,
  });
};

const handleUpdate = (...args) => {
  if (args.length) return updateElement(...args);
  return updateElement(elementData.value);
};

const isElementVisible = computed(() => elementData.value?.style?.additional?.visible !== "0");
</script>

<template>
  <div class="element" :class="{ 'popup-wrapper': popup?.enabled }">
    <!-- бейдж про popup -->
    <div class="displayed-in-popup-alert m-2" v-if="popup?.enabled">
      {{ t('element.displayedInPopupAlert') }}
    </div>

    <!-- Кнопка "добавить сверху" -->
    <AddNewElementButton v-if="isActive" :data="{ pageId: props.pageId, order: elementData.order }"
      :websiteId="props.websiteId" :elementId="props.prev?.id ?? 0" @click.stop />

    <!-- Кнопка открытия BlockStyler -->
    <div class="block-styler-btn" v-if="isActive" @click.stop>
      <router-link :to="{
        name: 'pages.element.style',
        params: {
          websiteId: props.websiteId,
          pageId: props.pageId,
          elementId: elementData.id,
        },
      }">
        <Icon icon="carbon:color-palette" width="48" height="48" />
      </router-link>
    </div>

    <!-- Редактирование / Копирование -->
    <EditElementButton class="edit-btn" v-if="isActive" :websiteId="props.websiteId" :pageId="props.pageId"
      :elementId="elementData.id" :element="elementData" @click.stop />
    <VisibilityElement class="visibility-btn" v-if="isActive" :websiteId="props.websiteId" :pageId="props.pageId"
      :element="elementData" @click.stop />
    <CopyElementButton class="copy-btn" v-if="isActive" :websiteId="props.websiteId" :pageId="props.pageId"
      :element="elementData" @click.stop />
    <StyleCopyButton class="style-copy-btn-pos" v-if="isActive && !props.hideStyleCopy" :element="elementData" @click.stop
      @pasted="handleUpdate" />

    <!-- Перестановка элементов -->
    <ReorderElement v-if="isActive" :current="elementData" :prev="props.prev" :next="props.next" :pageId="props.pageId"
      @click.stop />

    <!-- Сам элемент -->
    <div @click="handleToggleActive" :class="{ selected: isActive, 'element-hidden': !isElementVisible }">
      <component :is="renderElement(props.type, 'element')" :pageId="props.pageId" :data="elementData"
        @create="handleCreate" @update="handleUpdate" @create-element="handleCreate" 
        @update-element="handleUpdate" 
        />
    </div>

    <!-- Кнопка "добавить снизу" -->
    <AddNewElementButton v-if="isActive" :data="{ pageId: props.pageId, order: (elementData.order ?? 0) + 1 }"
      :websiteId="props.websiteId" :elementId="elementData.id" position="down" @click.stop />

    <!-- Кнопка попапа в админке (только визуально) -->
    <div class="col-12 popup-btn" v-if="popup?.enabled" :class="popupAlignClass">
      <span class="btn" :class="popup?.style?.widthClass || 'w-auto'" :style="popupStyle">
        {{ popup?.value?.text || 'Open' }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.element {
  position: relative;
  max-width: 100%;
}

.popup-wrapper {
  border: 2px solid #5e5e5e;
  margin: 10px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.selected {
  border: 1px dotted #5e5e5e;
  margin: 16px 0;
  min-height: 224px;
}

.element-hidden {
  opacity: 0.3;
}

.block-styler-btn {
  position: absolute;
  z-index: 790;
  left: 50%;
  top: 35%;
  margin-left: -72px;
  margin-top: -24px;
  background: #fff;
  padding: 6px;
  border-radius: 6px;
  color: #000;
  border: 2px solid #000;

  a,
  &:hover,
  &:visited {
    color: #000;
  }
}

.edit-btn {
  position: absolute;
  z-index: 790;
  left: 50%;
  top: 35%;
  margin-left: 32px;
  margin-top: -24px;
}

.displayed-in-popup-alert {
  color: #5e5e5e;
  font-size: 0.7rem;
  text-align: center;
}

.visibility-btn {
  position: absolute;
  z-index: 790;
  left: calc(50% - 58px);
  top: 55%;
}

.copy-btn {
  position: absolute;
  z-index: 790;
  left: calc(50% + 32px);
  top: 55%;
}

.style-copy-btn-pos {
  position: absolute;
  z-index: 790;
  left: calc(50% + 90px);
  top: 55%;
}
</style>
